import { Flex, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/Elements';
import { InputField, SelectField } from '@/components/Form';
import { PinInputField } from '@/components/Form/PinInputField';
import { ASSETS_BUCKET_URL, CLIENT_ID, CLIENT_SECRET } from '@/config';
import {
  VALID_EMAIL,
  VALID_MOBILE_NUMBER,
  VALID_MOBILE_NUMBER_EMAIL_ID,
} from '@/constants/errorMessage';
import { useLogin } from '@/lib/auth';
import { useLoginWithMobileOTPMutation } from '@/modules/auth';
import { LoginTerms, convertToRegex } from '@/modules/auth/components';
import { useCartDetails } from '@/modules/cart/api/cartDetails';
import { useDeleteCart } from '@/modules/cart/api/deleteCart';
import { setStoreAuthResult } from '@/services/auth';
import { getOtpLength } from '@/services/user';
import { IStoredAuth, LoginWithMobileOTPResponse } from '@/types/auth';
import storage from '@/utils/storage';

export type LoginWithMobileOTPValues = {
  employeeId: string;
  city: string;
  cafeteria: string;
};

type loginmobileotpformProps = {
  onSuccess: () => void;
};

export const LoginWithMobileOTPForm = ({ onSuccess }: loginmobileotpformProps) => {
  const [otpStatus, setOtpStatus] = useState(false);
  const [response, setResponse] = useState<LoginWithMobileOTPResponse>();
  const sessionLocation = sessionStorage.getItem('location');
  const locationMap = storage.getItem('locationMap');
  const currentLocation = storage.getItem('currentLocation');

  const [cities, setCities] = useState<any>([]);
  const config = storage.getItem('config');
  const groupLocation = config?.group_location;
  const getAllCafeterias = useCallback(
    () =>
      locationMap &&
      Object.keys(locationMap).map((key) => ({
        label: locationMap[key].name,
        value: locationMap[key].id,
      })),
    [locationMap]
  );
  const [cafeteria, setCafeteria] = useState<any>(groupLocation ? [] : getAllCafeterias());

  const { mutateAsync: loginWithMobileOTP, isLoading: isLoginWithMobileOtpLoading } =
    useLoginWithMobileOTPMutation();
  const { mutateAsync: login, isLoading: isloginLoading } = useLogin();

  const deleteCartMutation = useDeleteCart();
  const { data: cartData } = useCartDetails({
    home_screen: false,
  });

  const isMobile = useMediaQuery('(max-width: 768px)', true);
  const onlyNumberRegex = new RegExp('^[0-9]*$');
  const mobileRegex = new RegExp('^[0-9]{10}$');
  const emailRegex = config?.email_pattern
    ? convertToRegex(config?.email_pattern)
    : new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$");
  function validateEmployeeId() {
    const otpPref = config.otp_preference;
    if (otpPref) {
      if (otpPref.send_on_email && otpPref.send_on_mobile) {
        return z.string().superRefine((value, ctx) => {
          if (onlyNumberRegex.test(value) && value) {
            if (!mobileRegex.test(value)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: VALID_MOBILE_NUMBER,
              });
            }
            return z.NEVER;
          }

          if (!emailRegex.test(value) && value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: VALID_EMAIL,
            });
          }
          if (!value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: VALID_MOBILE_NUMBER_EMAIL_ID,
            });
          }
        });
      }
      if (otpPref.send_on_email) {
        return z.string().email({ message: VALID_EMAIL }).regex(emailRegex, {
          message: VALID_EMAIL,
        });
      }
      if (otpPref.send_on_mobile) {
        return z
          .string()
          .regex(mobileRegex, { message: VALID_MOBILE_NUMBER })
          .min(10, { message: VALID_MOBILE_NUMBER })
          .max(10, { message: VALID_MOBILE_NUMBER });
      }
    }
    return z
      .string()
      .min(10, {
        message: VALID_MOBILE_NUMBER,
      })
      .max(10, {
        message: VALID_MOBILE_NUMBER,
      });
  }

  const schema: any = groupLocation
    ? z.object({
        employeeId: validateEmployeeId(),
        city: z.number().or(z.string().min(1, { message: 'Please select a city' })),
        cafeteria: z.number().or(z.string().min(1, { message: 'Please select a cafeteria' })),
      })
    : z.object({
        employeeId: validateEmployeeId(),
        cafeteria: z.number().or(z.string().min(1, { message: 'Please select a cafeteria' })),
      });

  const loginmobileotpform = useForm<LoginWithMobileOTPValues>({
    initialValues: sessionLocation
      ? {
          employeeId: '',
          city: currentLocation?.city_id,
          cafeteria: currentLocation?.id,
        }
      : {
          employeeId: '',
          city: '',
          cafeteria: '',
        },
    validate: zodResolver(schema),
    validateInputOnBlur: true,
  });

  const validateOTPform = useForm<{ otp: string }>({
    initialValues: {
      otp: '',
    },
    validate: {
      otp: (value) => (value.length === 0 ? 'OTP is required' : null),
    },
  });

  const getMaxLength = (): number => (config?.otp_preference?.send_on_email ? 100 : 10);
  const getLabel = (): string => (config?.login_methods_pwa ?? ['Phone No']).join(' / ');
  const getType = (): 'password' | 'text' | 'email' | 'tel' | 'number' => {
    if (config?.otp_preference?.send_on_email && config?.otp_preference?.send_on_mobile) {
      return 'text';
    }
    if (config?.otp_preference?.send_on_email) {
      return 'email';
    }
    return 'tel';
  };

  const getCities = useCallback(
    () =>
      locationMap &&
      Object.keys(locationMap)
        .map((key) => ({
          label: locationMap[key].city_name,
          value: locationMap[key].city_id,
        }))
        .filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i),
    [locationMap]
  );

  const getCafeteria = useCallback(
    (cityId: string) =>
      locationMap &&
      Object.keys(locationMap)
        .filter((key) => locationMap[key].city_id === cityId)
        .map((key) => ({
          label: locationMap[key].name,
          value: locationMap[key].id,
        })),
    [locationMap]
  );

  useEffect(() => {
    if (cartData) {
      const locationData = locationMap[cartData?.cart_data?.location_id];
      if (locationData) {
        if (groupLocation) {
          loginmobileotpform.setFieldValue('city', locationData.city_id);
        }
        loginmobileotpform.setFieldValue('cafeteria', locationData.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData]);

  useEffect(() => {
    const cityNames = getCities();
    setCities(cityNames);
    if (!groupLocation) {
      const data = getAllCafeterias();
      setCafeteria(data);
    }
  }, []);

  useEffect(() => {
    if (loginmobileotpform.values.city !== currentLocation?.city_id) {
      loginmobileotpform.setFieldValue('cafeteria', '');
    }
    if (loginmobileotpform.values.city) {
      const data = getCafeteria(loginmobileotpform.values.city);
      setCafeteria(data);
    }
  }, [loginmobileotpform.values.city]);

  const updateData = async (cafeteriaId: string) => {
    sessionStorage.removeItem('currentOccasion');
    storage.removeItem('currentOccasion');
    storage.removeItem('occasions');
    const cafeteria = locationMap[cafeteriaId] ?? Object.values(locationMap)[0];
    storage.setItem('currentLocation', cafeteria);
    sessionStorage.setItem('currentLocation', JSON.stringify(cafeteria));
  };

  const handleSendOTP = async (values: LoginWithMobileOTPValues) => {
    try {
      const company = storage.getItem('company');
      await updateData(values.cafeteria);
      if (currentLocation?.id !== values.cafeteria) {
        storage.removeItem('pwaQRLocationId');
      }

      if (!company.id) {
        return;
      }
      const payload = {
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        company_id: company.id,
        location_id: Number(values.cafeteria),
        user_identifier: values.employeeId,
      };
      const data = await loginWithMobileOTP(payload);

      if (data) {
        setOtpStatus(true);
        setResponse(data);
      }
    } catch (err: any) {}
  };

  const handleLoginWithMobileOTP = async (values: { otp: string }) => {
    try {
      const company = storage.getItem('company');
      const mobileNo = loginmobileotpform.values.employeeId;
      const newLocation = locationMap[loginmobileotpform.values.cafeteria] ?? currentLocation;
      const selectedCafeteria = loginmobileotpform.values.cafeteria;
      storage.setItem('currentLocation', newLocation);
      if (!company.id) {
        return;
      }

      if (
        cartData?.cart_data &&
        Number(selectedCafeteria) !== Number(cartData?.cart_data?.location_id)
      ) {
        void deleteCartMutation.mutateAsync({
          cart_ref: cartData.cart_data.cart_ref,
          version: cartData.cart_data.version,
        });
      }

      const payload = {
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        company_id: company.id,
        grant_type: 'password',
        location_id: currentLocation?.id,
        login_otp: Number(values.otp),
        user_identifier: mobileNo,
        password: '',
        username: '',
      };
      const loginResponse = await login(payload);
      if (loginResponse) {
        const authData: IStoredAuth = {
          webAccessToken: loginResponse.access_token,
          refreshToken: loginResponse.refresh_token,
          expiresIn: loginResponse.expires_in,
          scope: loginResponse.scope,
        };
        setStoreAuthResult(authData);
      }
      onSuccess();
    } catch (err: any) {}
  };

  return (
    <>
      {!otpStatus ? (
        <form
          onSubmit={loginmobileotpform.onSubmit((values) => {
            void handleSendOTP(values);
          })}
        >
          <Text fw={600} component="p" my="md">
            We will send you an OTP
          </Text>

          <InputField
            type={getType()}
            label={getLabel()}
            maxLength={getMaxLength()}
            register={{ ...loginmobileotpform.getInputProps('employeeId') }}
            mt="lg"
            mb="xl"
          />

          <Flex direction="column" mb="lg">
            <Text fw={600} component="p" mb="2px">
              {groupLocation ? 'Select your city & cafeteria:' : 'Select your cafeteria:'}
            </Text>
            {cartData?.cart_data?.cart_items
              ? Object.keys(cartData?.cart_data?.cart_items).length > 0 && (
                  <Flex align="center">
                    <img
                      src={`${ASSETS_BUCKET_URL}/svg/Warning.svg`}
                      alt="Warning"
                      loading="lazy"
                      className="sm-icon"
                    />
                    <Text c="dark" fz={{ base: 'sm', sm: 'md' }} lh={1.2} fw={400}>
                      Changing the cafeteria will clear your cart.
                    </Text>
                  </Flex>
                )
              : null}
          </Flex>

          {groupLocation && (
            <SelectField
              label="City"
              data={cities}
              register={{ ...loginmobileotpform.getInputProps('city') }}
              mb={{ base: 'sm', sm: 'md' }}
              dropdownPosition={!isMobile ? 'bottom' : 'top'}
            />
          )}
          <SelectField
            label="Cafeteria"
            data={cafeteria}
            register={{ ...loginmobileotpform.getInputProps('cafeteria') }}
            // dropdownPosition={!isMobile ? 'bottom' : 'top'}
            mb="xl"
          />
          <div className="auth-buttons-spacing">
            <Button
              loading={isLoginWithMobileOtpLoading}
              type="submit"
              fullWidth
              className={!loginmobileotpform.isValid() ? 'disabled-button' : ''}
            >
              Get OTP
            </Button>
          </div>
          <LoginTerms />
        </form>
      ) : (
        <form
          onSubmit={validateOTPform.onSubmit((values) => {
            void handleLoginWithMobileOTP(values);
          })}
        >
          <Text fw={600} component="p" my="lg">
            {response?.message}
          </Text>

          <PinInputField
            length={getOtpLength(response)}
            register={{ ...validateOTPform.getInputProps('otp') }}
            type="number"
            mb="lg"
            autoFocus
          />

          <Button
            loading={isloginLoading}
            type="submit"
            fullWidth
            className={
              !validateOTPform.isValid()
                ? 'disabled-button auth-buttons-spacing'
                : 'auth-buttons-spacing'
            }
          >
            Verify
          </Button>
        </form>
      )}
    </>
  );
};
